import { NgIf } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { ModalComponent } from '@components/modal/modal.component';
import { environment } from '@environments/environment';
import { Map, NgFindalabModule, ResultService } from '@Medology/ng-findalab';
import { MapsLoadService } from '@services/maps-load.service';

@Component({
  selector: 'app-lab-finder-modal',
  templateUrl: './lab-finder-modal.component.html',
  styleUrls: ['./lab-finder-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [ModalComponent, NgIf, NgFindalabModule],
})
export class LabFinderModalComponent implements OnInit {
  @Input() public active: boolean = false;
  @Input() public searchDescription: string = '';

  @Output() public activeChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  public map: Map = null;

  private mapsLoadService = inject(MapsLoadService);
  private resultService = inject(ResultService);

  /**
   * Initializes the component.
   */
  public ngOnInit(): void {
    this.loadMap();
  }

  /**
   * Closes the modal.
   */
  public closeModal(): void {
    this.active = false;
    this.activeChange.emit(this.active);
  }

  /**
   * Gets the map configuration based on the provided postal code.
   *
   * @param {string} zipcode the postal code to search for labs
   *
   * @returns {Map} the configured map
   */
  private getMapConfig(zipcode: string): Map {
    let map = {
      searchUrl: {
        hostname: `${environment.analyteCareApi}/api/v1`,
        path: '/labs/nearPostalCode',
      },
      apiKey: environment.googleMapApiKey,
      testing: !environment.production,
      searchFunction: {
        filterByTests: null,
      },
      lab: {
        phone: {
          show: false,
        },
        button: {
          show: false,
        },
      },
      search: {
        title: 'Enter Your Location',
        description: this.searchDescription,
        button: {
          class: `button ${environment.styles.testCenter.searchButtonClass}`,
          loadingText: '...',
          text: environment.ngFindalab.searchButtonText,
          icon: {
            show: environment.ngFindalab.showSearchIcon,
            class: 'fa fa-search',
            loadingClass: 'fa fa-spinner fa-spin',
          },
        },
        placeholder: 'Enter your zip',
        inputType: 'tel',
        showDescription: true,
        messageNoResults:
          'Oops! Sorry, we could not find any testing centers near that location. Please try your search again with a different or less specific address.',
      },
      userLocation: {
        showOption: true,
        icon: 'fa fa-map-marker',
        msg: environment.ngFindalab.locateMsg,
        loading: {
          icon: 'fa fa-spin fa-spinner',
          msg: 'Searching current location...',
        },
      },
      dayOfWeekFilter: {
        showOption: true,
        radioAllText: 'All',
        radioDaysText: {
          0: null,
          1: null,
          2: null,
          3: null,
          4: null,
          5: null,
          6: 'Open Saturdays',
        },
      },
      postalCode: zipcode ?? null,
      mobileBreakpoint: 848,
      googleMaps: {
        defaultLat: 39.97712,
        defaultLong: -99.587403,
        geoCoder: null,
        infoWindow: null,
        initialZoom: 4,
        map: null,
        markers: [],
        resultsZoom: environment.ngFindalab.resultsZoom,
        labMarkerFillColor: environment.styles.testCenter.googleMapsLabMarkerFillColor,
        recommendedMarkerFillColor: environment.styles.testCenter.googleMapsRecommendedMarkerFillColor,
        markerHoverFillColor: environment.styles.testCenter.googleMapsMarkerHoverFillColor,
      },
      mapId: environment.ngFindalab.findalabMapId,
    };

    return map as Map;
  }

  /**
   * Loads the map configuration.
   */
  private loadMap(): void {
    this.mapsLoadService
      .getGoogleMapApi(environment.googleMapApiKey)
      .subscribe(() => (this.map = this.getMapConfig(this.resultService.getPostalCode())));
  }
}
